/**
 * What Qrvey's diagnostics role may read inside an instance's cluster, when the
 * customer opts in (IM-204, dispatcher v19).
 *
 * Most of what broke on 24/7 NV — NodePool limits, starved pods, an autoscaler
 * thrashing — is visible only through the Kubernetes API, and the diagnostics
 * role has no access to it. The control plane is read-only and cannot grant
 * itself any, so the access is made in the customer's account, by the runner,
 * under the grant, and only when the dispatcher stack says `ClusterDiagnostics`
 * is `read-only`.
 *
 * ## Why a role of our own, and not an AWS access policy
 *
 * `AmazonEKSViewPolicy` reads none of the custom resources that matter here —
 * NodePools, NodeClaims, ScaledObjects, Kueue's queues — and grants a long list
 * of built-in kinds nothing here asks about. So the diagnostics role's access
 * entry carries **no** access policy, only the Kubernetes group below, and this
 * ClusterRole is what that group may do: `get` and `list`, on the kinds named,
 * and nothing else.
 *
 * ## What it never reads
 *
 * Secrets and ConfigMaps, a pod's logs, `exec`, `attach`, port-forwarding, and
 * any service proxy but one. A proxy reaches the application's own APIs, which
 * means customer data, so the ClusterRole grants none; Prometheus's is a Role
 * of its own, below. `cluster-diagnostics.test.ts` holds each of these against
 * the rules, and against the golden files the customer's security team can
 * read.
 *
 * ## The one proxy: Prometheus, GET only (IM-207, dispatcher v20)
 *
 * Why NV's Prometheus was evicted, and what Kong answered, are in Prometheus's
 * own record and nowhere else. It is reached through the API server's service
 * proxy, the only path into a cluster this control plane has, and the
 * permission names it exactly: `get` on `services/proxy`, in `monitoring`
 * alone, for the one resource name `PROMETHEUS_PROXY_NAME`. RBAC checks a
 * proxy's name as the URL segment carries it, port included, so another
 * service, another port of this one, or the same name in another namespace is
 * refused; and `get` is a GET, so nothing that changes Prometheus — an admin
 * API's delete, a `/-/quit` — can be asked. The name is the installer's,
 * measured on `es9kp25n` (2026-11-02), not guessed.
 *
 * It is a Role rather than a rule in the ClusterRole because a ClusterRole's
 * `resourceNames` hold in every namespace, and applied apart from it because
 * a cluster without a `monitoring` namespace must still get its other reads.
 *
 * The API server reaches Prometheus from the cluster's control plane, which
 * the installer's node security group does not admit on 9090 — it admits only
 * the EKS module's recommended ports. So the runner adds that one rule as
 * well, from the cluster's own control-plane group alone, and removes it when
 * the setting goes back to `off` (`runner-wrapper.ts`).
 *
 * Pod and workload specs do carry environment variables written literally,
 * which the installer uses for some settings. That is the price of seeing why a
 * pod is pending; the control plane keeps only a projection of what it reads
 * (IM-205), never the specs.
 */
/** The Kubernetes group the diagnostics role's access entry carries. */
export const CLUSTER_DIAGNOSTICS_GROUP = 'qrvey-diagnostics';
/** The ClusterRole and its binding share this name. */
export const CLUSTER_DIAGNOSTICS_NAME = 'qrvey-diagnostics';
/**
 * The dispatcher stack's `ClusterDiagnostics` parameter. `off` is the default
 * in every account, and turning it back to `off` removes the access on the next
 * run.
 */
export const CLUSTER_DIAGNOSTICS_MODES = ['off', 'read-only'];
/** The only verbs the role holds. */
export const CLUSTER_DIAGNOSTICS_VERBS = ['get', 'list'];
const read = (apiGroup, ...resources) => ({
    apiGroups: [apiGroup],
    resources,
    verbs: [...CLUSTER_DIAGNOSTICS_VERBS],
});
/**
 * One rule per API group, each naming its kinds. No wildcard anywhere: a `*`
 * would grant a kind somebody installs next year.
 */
export const CLUSTER_DIAGNOSTICS_RULES = [
    read('', 'nodes', 'namespaces', 'pods', 'events', 'services', 'endpoints', 'persistentvolumeclaims'),
    read('apps', 'deployments', 'statefulsets', 'daemonsets', 'replicasets'),
    read('batch', 'jobs', 'cronjobs'),
    read('autoscaling', 'horizontalpodautoscalers'),
    read('policy', 'poddisruptionbudgets'),
    read('events.k8s.io', 'events'),
    read('karpenter.sh', 'nodepools', 'nodeclaims'),
    read('karpenter.k8s.aws', 'ec2nodeclasses'),
    read('keda.sh', 'scaledobjects', 'scaledjobs'),
    read('kueue.x-k8s.io', 'clusterqueues', 'localqueues', 'workloads'),
    read('monitoring.coreos.com', 'prometheuses', 'alertmanagers', 'alertmanagerconfigs', 'servicemonitors'),
    read('metrics.k8s.io', 'pods', 'nodes'),
];
/**
 * The installer's Prometheus service: kube-prometheus-stack's, as the
 * installer names its release. Read by the control plane, and the only name
 * the proxy Role grants.
 */
export const PROMETHEUS_SERVICE = {
    namespace: 'monitoring',
    name: 'prometheus-kube-prometheus-prometheus',
    port: 9090,
};
/**
 * What the runner's security group rule says it is, so a person reading the
 * group in the console knows whose it is and why. Only characters EC2 accepts
 * in a rule description, and none JSON or the shell would need to escape.
 */
export const PROMETHEUS_PORT_DESCRIPTION = 'qrvey-diagnostics: the cluster control plane reads Prometheus';
/** The proxy's resource name, as the request path carries it and RBAC checks it. */
export const PROMETHEUS_PROXY_NAME = `${PROMETHEUS_SERVICE.name}:${String(PROMETHEUS_SERVICE.port)}`;
/** The proxy Role's only rule: one verb, one subresource, one name. */
export const PROMETHEUS_PROXY_RULE = {
    apiGroups: [''],
    resources: ['services/proxy'],
    resourceNames: [PROMETHEUS_PROXY_NAME],
    verbs: ['get'],
};
/** Marks what the runner made, so a person reading the cluster knows whose it is. */
const LABELS = {
    'app.kubernetes.io/name': CLUSTER_DIAGNOSTICS_NAME,
    'app.kubernetes.io/managed-by': 'qrvey-runner',
};
/**
 * The ClusterRole and its binding to the group, as one `List` the runner hands
 * to `kubectl apply`. JSON rather than YAML so it needs nothing to render, and
 * applied on every run that sets the mode, so a newer dispatcher's rules replace
 * an older one's.
 */
export const clusterDiagnosticsManifest = () => ({
    apiVersion: 'v1',
    kind: 'List',
    items: [
        {
            apiVersion: 'rbac.authorization.k8s.io/v1',
            kind: 'ClusterRole',
            metadata: { name: CLUSTER_DIAGNOSTICS_NAME, labels: LABELS },
            rules: CLUSTER_DIAGNOSTICS_RULES,
        },
        {
            apiVersion: 'rbac.authorization.k8s.io/v1',
            kind: 'ClusterRoleBinding',
            metadata: { name: CLUSTER_DIAGNOSTICS_NAME, labels: LABELS },
            roleRef: {
                apiGroup: 'rbac.authorization.k8s.io',
                kind: 'ClusterRole',
                name: CLUSTER_DIAGNOSTICS_NAME,
            },
            subjects: [
                {
                    apiGroup: 'rbac.authorization.k8s.io',
                    kind: 'Group',
                    name: CLUSTER_DIAGNOSTICS_GROUP,
                },
            ],
        },
    ],
});
/**
 * The Prometheus proxy Role and its binding to the same group, as a `List` of
 * its own (IM-207). Applied after the ClusterRole and apart from it, so a
 * cluster with no `monitoring` namespace refuses only this.
 */
export const prometheusProxyManifest = () => ({
    apiVersion: 'v1',
    kind: 'List',
    items: [
        {
            apiVersion: 'rbac.authorization.k8s.io/v1',
            kind: 'Role',
            metadata: {
                name: CLUSTER_DIAGNOSTICS_NAME,
                namespace: PROMETHEUS_SERVICE.namespace,
                labels: LABELS,
            },
            rules: [PROMETHEUS_PROXY_RULE],
        },
        {
            apiVersion: 'rbac.authorization.k8s.io/v1',
            kind: 'RoleBinding',
            metadata: {
                name: CLUSTER_DIAGNOSTICS_NAME,
                namespace: PROMETHEUS_SERVICE.namespace,
                labels: LABELS,
            },
            roleRef: {
                apiGroup: 'rbac.authorization.k8s.io',
                kind: 'Role',
                name: CLUSTER_DIAGNOSTICS_NAME,
            },
            subjects: [
                {
                    apiGroup: 'rbac.authorization.k8s.io',
                    kind: 'Group',
                    name: CLUSTER_DIAGNOSTICS_GROUP,
                },
            ],
        },
    ],
});
//# sourceMappingURL=cluster-diagnostics.js.map